import React, { useState, useEffect, useCallback } from 'react';
import api, { callsAPI, leadsAPI } from '../services/api';

// « Historique » (Karim 2026-10-22) : agents must see their calling history and
// search leads they called in the campaign. Two tabs:
//   • Mes appels   — the agent's OWN calls (name+phone+disposition+notes)
//   • Recherche    — free-text search (phone/name/email) over ALL campaign
//                    leads (Karim picked all-campaign scope 2026-10-22)
// Backend already serves both (GET /api/calls?agent_id=…, GET /api/leads?search=…)
// — this is pure frontend. PERMANENT RULE (Karim): every lead row shows the
// FULL name AND phone, never one or the other.

// Toronto-today as 'YYYY-MM-DD' — the agents' day boundary is Quebec local,
// not server UTC (same model as the admin counter fix 2026-10-20).
const torontoDate = (offsetDays = 0) => {
  const p = {};
  new Intl.DateTimeFormat('en-CA', { timeZone:'America/Toronto', year:'numeric', month:'2-digit', day:'2-digit' })
    .formatToParts(new Date(Date.now() + offsetDays * 86400000)).forEach(it => p[it.type] = it.value);
  return `${p.year}-${p.month}-${p.day}`;
};

const PERIODS = [
  { key:'today', label:"Aujourd'hui",  from:() => torontoDate(0),   to:null },
  { key:'yday',  label:'Hier',         from:() => torontoDate(-1),  to:() => torontoDate(-1) },
  { key:'week',  label:'7 jours',       from:() => torontoDate(-6),  to:null },
  { key:'month', label:'Ce mois-ci',   from:() => torontoDate(0).slice(0,8) + '01', to:null },
];

const fmtTime = s => { try { return new Date(s).toLocaleString('fr-CA', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }); } catch { return ''; } };

function Chip({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding:'6px 12px', borderRadius:100, cursor:'pointer', fontSize:11, fontWeight:800,
      textTransform:'uppercase', letterSpacing:'0.04em',
      background: active ? 'linear-gradient(135deg,var(--purple),var(--indigo))' : 'var(--bg-input)',
      color: active ? '#fff' : 'var(--text2)', border:'none', flexShrink:0,
    }}>{children}</button>
  );
}

// One lead row — click expands the full lead details (read-only).
function LeadRow({ lead, expanded, onToggle }) {
  return (
    <div style={{ borderBottom:'1px solid var(--border-dim)' }}>
      <div onClick={onToggle} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', cursor:'pointer' }}
           onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
           onMouseLeave={e => e.currentTarget.style.background = 'none'}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:800, color:'var(--text)' }}>
            {lead.first_name} {lead.last_name}
            {lead.status === 'completed' && <span style={{ fontSize:9, marginLeft:6, color:'var(--green)', fontWeight:800 }}>TERMINÉ</span>}
          </div>
          <div style={{ fontSize:10, color:'var(--text2)' }}>
            {lead.last_disposition ? `Dernier résultat : ${lead.last_disposition}` : 'Jamais dispositioné'}
            {lead.agent_name ? ` · agent : ${lead.agent_name}` : ''}
          </div>
        </div>
        <div style={{ fontFamily:'var(--mono)', fontSize:13, fontWeight:700, color:'var(--text)', flexShrink:0 }}>{lead.phone}</div>
      </div>
      {expanded && (
        <div style={{ padding:'8px 14px 12px', background:'var(--bg-elevated)', fontSize:11, color:'var(--text2)', lineHeight:1.8 }}>
          <div>✉️ {lead.email || '—'}</div>
          <div>📍 {[lead.address, lead.city, lead.province, lead.postal_code].filter(Boolean).join(', ') || '—'}</div>
          <div>Statut : {lead.status}{lead.attempts != null ? ` · tentatives : ${lead.attempts}` : ''}</div>
        </div>
      )}
    </div>
  );
}

export default function CallHistory({ user, onClose }) {
  const [tab, setTab] = useState('mine');            // 'mine' | 'search'
  const [period, setPeriod] = useState('today');
  const [calls, setCalls] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const loadMine = useCallback(async (pKey) => {
    const p = PERIODS.find(x => x.key === pKey) || PERIODS[0];
    setLoading(true); setError(null);
    try {
      const params = { agent_id: user?.id, date_from: p.from(), limit: 100 };
      if (p.to) params.date_to = p.to();
      const d = await callsAPI.list(params);
      setCalls(d?.calls || []);
    } catch (err) { setError(err?.error || err?.message || 'Erreur de chargement'); setCalls([]); }
    finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { if (tab === 'mine') loadMine(period); }, [tab, period, loadMine]);

  const doSearch = async () => {
    const s = q.trim();
    if (s.length < 2) return;
    setLoading(true); setError(null); setExpanded(null);
    try {
      const d = await leadsAPI.searchAll(s);
      setResults(d?.leads || []);
    } catch (err) { setError(err?.error || err?.message || 'Erreur de recherche'); setResults([]); }
    finally { setLoading(false); }
  };

  return (
    <div className="dispo-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dispo-modal" style={{ width:'min(92vw, 720px)', maxWidth:'92vw', maxHeight:'85vh', display:'flex', flexDirection:'column' }}>

        <div className="dispo-modal-header">
          <span>📜 Historique — {user?.full_name || user?.username}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text2)', cursor:'pointer', fontSize:18 }}>✕</button>
        </div>

        <div style={{ padding: 16, overflow:'auto', flex:1 }}>
          {/* Tabs */}
          <div style={{ display:'flex', gap:6, marginBottom:14 }}>
            <Chip active={tab === 'mine'} onClick={() => setTab('mine')}>📞 Mes appels</Chip>
            <Chip active={tab === 'search'} onClick={() => setTab('search')}>🔍 Recherche</Chip>
          </div>

          {error && (
            <div style={{ padding:'10px 12px', marginBottom:12, borderRadius:10, fontSize:12, fontWeight:700, color:'var(--red)', background:'rgba(239,68,68,0.12)', border:'2px solid var(--red)' }}>
              ⚠️ {error}
            </div>
          )}

          {tab === 'mine' && (
            <>
              <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
                {PERIODS.map(p => <Chip key={p.key} active={period === p.key} onClick={() => setPeriod(p.key)}>{p.label}</Chip>)}
              </div>
              {loading && <div style={{ fontSize:12, color:'var(--text2)', padding:20, textAlign:'center' }}>Chargement…</div>}
              {!loading && calls && calls.length === 0 && (
                <div style={{ fontSize:12, color:'var(--text2)', padding:20, textAlign:'center' }}>Aucun appel sur cette période.</div>
              )}
              {!loading && calls && calls.length > 0 && calls.map(c => (
                <div key={c.id} style={{ borderBottom:'1px solid var(--border-dim)', padding:'9px 12px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--text2)', flexShrink:0, width:92 }}>
                      {fmtTime(c.called_at)}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:800, color:'var(--text)' }}>
                        {c.first_name} {c.last_name}
                      </div>
                      {c.notes && <div style={{ fontSize:10, color:'var(--text2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>📝 {c.notes}</div>}
                    </div>
                    <div style={{ fontFamily:'var(--mono)', fontSize:13, fontWeight:700, color:'var(--text)', flexShrink:0 }}>{c.lead_phone}</div>
                    {c.disposition_label && (
                      <span style={{ flexShrink:0, fontSize:9, padding:'2px 8px', borderRadius:100, fontWeight:800, background:(c.disposition_color || '#94a3b8') + '22', color:c.disposition_color || 'var(--text2)' }}>
                        {c.disposition_label}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}

          {tab === 'search' && (
            <>
              <div style={{ display:'flex', gap:6, marginBottom:12 }}>
                <input
                  className="inp" autoFocus style={{ flex:1, fontSize:13 }}
                  placeholder="Téléphone, nom ou courriel…"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && doSearch()}
                />
                <button className="btn btn-blue" onClick={doSearch} disabled={q.trim().length < 2}>🔍</button>
              </div>
              <div style={{ fontSize:10, color:'var(--text2)', marginBottom:10 }}>
                Recherche sur toutes les campagnes de votre compte.
              </div>
              {loading && <div style={{ fontSize:12, color:'var(--text2)', padding:20, textAlign:'center' }}>Recherche…</div>}
              {!loading && results && results.length === 0 && (
                <div style={{ fontSize:12, color:'var(--text2)', padding:20, textAlign:'center' }}>Aucun lead trouvé.</div>
              )}
              {!loading && results && results.map(l => (
                <LeadRow key={l.id} lead={l} expanded={expanded === l.id} onToggle={() => setExpanded(expanded === l.id ? null : l.id)} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
